<template>
    <DynamicScroller
        v-show="!isEmpty"
        ref="dynamicScrollerEl"
        :items="modulesWithComponent"
        :min-item-size="20"
        class="scroller h-full w-full"
        @scroll-end="scrollEnd"
    >
        <template #default="{ item, active }">
            <DynamicScrollerItem
                :item="item"
                :active="active"
            >
                <component
                    :is="item.component"
                    v-if="item.component"
                    :config="item.config"
                    :data-cy="`module-component-${item.config.component}`"
                    :data-previous="item.previousComponent"
                    :filter="nestedFilter ? filter : null"
                    :search-term="searchTerm"
                    :search-properties="searchProperties"
                    :is-remote-search="isRemoteSearch"
                />
            </DynamicScrollerItem>
        </template>
    </DynamicScroller>

    <template v-if="isEmpty">
        <slot name="empty" />
    </template>
</template>

<script setup>
    import { useModuleStore } from '@/stores'
    import components from '@/constants/components'
    import componentTypes from '@/constants/componentTypes'
    import LoadingListItem from '@/components/ui/LoadingListItem.vue'

    const emit = defineEmits(['appendModules'])

    const props = defineProps({
        modules: Array,
        filter: Function,
        nestedFilter: Boolean,
        paginationUrl: String,
        searchTerm: String,
        searchProperties: Array,
        isRemoteSearch: Boolean,
    })

    const moduleStore = useModuleStore()
    const dynamicScrollerEl = useTemplateRef('dynamicScrollerEl')

    const modules = ref(props.modules ?? [])
    const currentPaginationUrl = ref(props.paginationUrl)
    const paginating = ref(false)

    const modulesWithComponent = computed(() => {
        let previousComponent = null
        const items = map(modules.value, (module, index) => {
            const item = {
                id: module.uuid,
                component: components[module.component],
                config: module,
                previousComponent,
            }
            previousComponent = module.component
            return item
        })

        if (currentPaginationUrl.value) {
            items.push({
                id: items.length,
                component: LoadingListItem,
                config: {},
                previousComponent,
            })
        }

        return items
    })

    const isEmpty = computed(() => {
        return modules.value.length === 0
    })

    const scrollEnd = (e) => {
        if (!currentPaginationUrl.value || paginating.value) {
            return
        }
        paginating.value = true
        moduleStore.loadContent(currentPaginationUrl.value)
            .then(({ data }) => {
                emit('appendModules', data.items)

                if (!data.items.length) {
                    currentPaginationUrl.value = null
                    return
                }

                // Update pagination url
                currentPaginationUrl.value = data.meta.next
            })
            .finally(() => {
                paginating.value = false
            })
    }

    watch(() => props.paginationUrl, (newVal) => {
        currentPaginationUrl.value = newVal
    })

    watch(() => props.modules, (newVal) => {
        modules.value = newVal ?? []
    })
</script>
